import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { ArrowRight } from "lucide-react";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    return <Navigate to={user.role === "super_admin" ? "/admin" : "/app"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const r = await login(email, pw);
    setBusy(false);
    if (!r.ok) setErr(r.error);
    else nav(r.user.role === "super_admin" ? "/admin" : "/app", { replace: true });
  };

  return (
    <div className="min-h-screen brand-curve grid lg:grid-cols-2">
      {/* LEFT — playful hero */}
      <div className="hidden lg:flex flex-col relative overflow-hidden" style={{ background: "linear-gradient(140deg, #FFF0DF 0%, #FFFFFF 50%, #E4EAF4 100%)" }}>
        <div className="p-10">
          <Link to="/" data-testid="login-logo"><Logo size={36}/></Link>
        </div>
        <div className="flex-1 flex flex-col justify-center px-14 pb-16 relative">
          <p className="text-[11px] uppercase tracking-[0.22em] font-black" style={{ color: "var(--brand-strong)" }}>Customer Experience · 10X</p>
          <h2 className="mt-4 text-4xl xl:text-5xl font-black tracking-tighter leading-[1.05]" style={{ color: "var(--ink-900)" }}>
            Turn every response<br/>into a decision.
          </h2>
          <p className="mt-5 text-[15px] max-w-md leading-relaxed" style={{ color: "var(--ink-500)" }}>
            CX10X — the delightful, AI-first CX platform. Capture feedback across every touch point and get boardroom-grade insight in real time.
          </p>
          <img src="/brand/mascot-builder.jpg" alt="" className="absolute right-4 bottom-4 w-64 h-64 object-contain mix-blend-multiply pointer-events-none"/>

          <div className="mt-10 grid grid-cols-3 gap-8 max-w-md relative z-10">
            {[
              { k: "27,400+", v: "Responses / day" },
              { k: "43", v: "Languages" },
              { k: "99.98%", v: "Uptime" },
            ].map((s) => (
              <div key={s.k}>
                <p className="text-2xl font-black tracking-tight" style={{ color: "var(--ink-900)" }}>{s.k}</p>
                <p className="text-[11px] mt-1 leading-snug" style={{ color: "var(--ink-500)" }}>{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT — form */}
      <div className="flex flex-col relative bg-white">
        <div className="lg:hidden px-6 py-6">
          <Link to="/" data-testid="login-logo-mobile"><Logo/></Link>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 lg:px-16 py-12">
          <div className="w-full max-w-md">
            <p className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: "var(--brand-strong)" }}>Sign in</p>
            <h1 className="mt-3 text-3xl font-black tracking-tighter" style={{ color: "var(--ink-900)" }}>Welcome back 👋</h1>
            <p className="text-[14px] mt-2" style={{ color: "var(--ink-500)" }}>Access your CX10X workspace.</p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--ink-500)" }}>Work email</Label>
                <Input
                  data-testid="login-email"
                  type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required autoFocus autoComplete="email"
                  className="mt-1.5 h-12 rounded-2xl border-slate-200"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--ink-500)" }}>Password</Label>
                  <Link to="/forgot" className="text-[11px] font-bold hover:underline" style={{ color: "var(--brand-strong)" }}>Forgot?</Link>
                </div>
                <Input
                  data-testid="login-password"
                  type="password" value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required autoComplete="current-password"
                  className="mt-1.5 h-12 rounded-2xl border-slate-200"
                  placeholder="••••••••"
                />
              </div>
              {err && (
                <p data-testid="login-error" className="text-[13px] px-3 py-2 rounded-xl" style={{ color: "var(--detractor)", backgroundColor: "var(--detractor-bg)", border: "1px solid rgba(220,38,38,0.2)" }}>{err}</p>
              )}
              <button
                data-testid="login-submit"
                disabled={busy} type="submit"
                className="btn-pill w-full h-12 text-[15px] group"
              >
                {busy ? "Signing in…" : <span className="inline-flex items-center gap-2">Sign in <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5"/></span>}
              </button>
            </form>

            <p className="text-[13px] mt-8 pt-6" style={{ borderTop: "1px solid var(--border)", color: "var(--ink-500)" }}>
              New to CX10X? <Link to="/signup" data-testid="login-signup-link" className="font-bold hover:underline" style={{ color: "var(--brand-strong)" }}>Create a workspace →</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
